import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { LogOut, TrendingUp, Target, Calendar, BookOpen, Award, ChevronRight, Wand2 } from 'lucide-react';
import TestGenerator from '../components/TestGenerator';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

export default function ParentDashboard() {
  const { user, API, getHeaders, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [parentWorksheets, setParentWorksheets] = useState([]);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API}/parent/dashboard`, { headers: getHeaders() });
      if (!res.ok) throw new Error();
      setData(await res.json());
    } catch { toast.error('Failed to load dashboard'); }
    finally { setLoading(false); }
  }, [API, getHeaders]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const handleLogout = async () => { await logout(); navigate('/', { replace: true }); };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFF8F0' }}>
      <div className="space-y-3 w-full max-w-md px-4">
        {[1,2,3,4].map(i => <div key={i} className="h-24 bg-[#F0E6D8] rounded-2xl animate-pulse" />)}
      </div>
    </div>
  );

  const student = data?.student || {};
  const streak = data?.streak || {};
  const todayDone = data?.today_completed;
  const last7 = (data?.last_7_days || []).map(d => ({
    day: new Date(d.date + 'T00:00:00').toLocaleDateString('en', { weekday: 'short' }),
    score: d.score,
    attempted: d.attempted,
  }));
  const weakAreas = data?.weak_areas || [];
  const recentSessions = data?.recent_sessions || [];

  return (
    <div className="min-h-screen pb-6" style={{ background: '#FFF8F0' }}>
      {/* Header */}
      <div className="px-4 pt-4 pb-2 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-extrabold text-[#1B4F8A]">Parent Dashboard</h1>
          <p className="text-xs font-bold text-slate-500">Monitoring: {student.name || 'Student'}</p>
        </div>
        <button data-testid="parent-logout-btn" onClick={handleLogout} className="p-2"><LogOut className="w-5 h-5 text-slate-400" /></button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 px-4 mb-4">
        {[{key:'overview',label:'Overview',icon:TrendingUp},{key:'weekly',label:'Weekly',icon:Calendar},{key:'areas',label:'Focus Areas',icon:Target},{key:'create_test',label:'Create Test',icon:Wand2}].map(t => (
          <button key={t.key} data-testid={`parent-tab-${t.key}`} onClick={() => setActiveTab(t.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-bold transition-all
              ${activeTab === t.key ? 'bg-[#1B4F8A] text-white' : 'bg-white text-slate-500 border-2 border-[#E5DACE]'}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      <div className="max-w-2xl mx-auto px-4">
        {activeTab === 'overview' && (
          <>
            {/* Today Status */}
            <Card className={`border-2 rounded-2xl mb-4 ${todayDone ? 'border-[#06D6A0] bg-[#06D6A0]/5' : 'border-[#FFD23F] bg-[#FFD23F]/5'}`}>
              <CardContent className="p-4 text-center">
                <p className="text-lg font-extrabold" style={{ color: todayDone ? '#06D6A0' : '#FF6B35' }}>
                  {todayDone ? "Today's Practice Complete!" : "Not Practiced Today"}
                </p>
                {data?.today_session && <p className="text-sm font-bold text-slate-500">Score: {data.today_session.score_percent}%</p>}
              </CardContent>
            </Card>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 mb-4" data-testid="parent-stats">
              <Card className="border-2 border-[#E5DACE] rounded-2xl"><CardContent className="p-3 text-center">
                <p className="text-2xl font-black text-[#FF6B35]">{streak.current_streak || 0}🔥</p>
                <p className="text-xs font-bold text-slate-500">Streak</p>
              </CardContent></Card>
              <Card className="border-2 border-[#E5DACE] rounded-2xl"><CardContent className="p-3 text-center">
                <p className="text-2xl font-black text-[#1B4F8A]">{streak.longest_streak || 0}</p>
                <p className="text-xs font-bold text-slate-500">Best Streak</p>
              </CardContent></Card>
              <Card className="border-2 border-[#E5DACE] rounded-2xl"><CardContent className="p-3 text-center">
                <p className="text-2xl font-black text-[#06D6A0]">{data?.student_profile?.class_level || '?'}</p>
                <p className="text-xs font-bold text-slate-500">Class</p>
              </CardContent></Card>
            </div>

            {/* Recent Sessions */}
            <h3 className="font-bold text-[#1B4F8A] text-sm mb-2">Recent Sessions</h3>
            <div className="space-y-2 mb-4">
              {recentSessions.slice(0, 5).map((s, i) => (
                <Card key={i} className="border-2 border-[#E5DACE] rounded-2xl">
                  <CardContent className="p-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold text-[#1B4F8A]">{s.date}</p>
                      <p className="text-xs text-slate-400">{s.questions_attempted} questions</p>
                    </div>
                    <span className={`text-lg font-black ${s.score_percent >= 70 ? 'text-[#06D6A0]' : s.score_percent >= 50 ? 'text-[#FFD23F]' : 'text-red-500'}`}>
                      {s.score_percent}%
                    </span>
                  </CardContent>
                </Card>
              ))}
              {recentSessions.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No sessions yet</p>}
            </div>
          </>
        )}

        {activeTab === 'weekly' && (
          <Card className="border-2 border-[#E5DACE] rounded-2xl">
            <CardContent className="p-4">
              <h3 className="font-bold text-[#1B4F8A] text-sm mb-3">Weekly Score Trend</h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={last7}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5DACE" />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fontWeight: 'bold' }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={v => `${v}%`} />
                  <Line type="monotone" dataKey="score" stroke="#FF6B35" strokeWidth={3} dot={{ fill: '#FF6B35', r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
              <div className="mt-4 space-y-1">
                {last7.map((d, i) => (
                  <div key={i} className="flex items-center justify-between text-xs font-bold">
                    <span className="text-slate-500 w-10">{d.day}</span>
                    <div className="flex-1 mx-3 h-2.5 bg-[#F0E6D8] rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${d.score}%`, background: d.score >= 70 ? '#06D6A0' : d.score >= 50 ? '#FFD23F' : '#E63946' }} />
                    </div>
                    <span className="text-[#1B4F8A] w-10 text-right">{d.score}%</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'areas' && (
          <>
            {weakAreas.length > 0 ? (
              <Card className="border-2 border-red-200 rounded-2xl mb-4">
                <CardContent className="p-4">
                  <h3 className="font-bold text-red-500 text-sm mb-3 flex items-center gap-1"><Target className="w-4 h-4" /> Areas Needing Attention</h3>
                  <ResponsiveContainer width="100%" height={weakAreas.length * 40 + 40}>
                    <BarChart data={weakAreas.map(w => ({ name: w.chapter?.replace(/_/g, ' ').slice(0, 20), accuracy: w.accuracy }))} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5DACE" />
                      <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} />
                      <YAxis dataKey="name" type="category" width={130} tick={{ fontSize: 10, fontWeight: 'bold' }} />
                      <Tooltip formatter={v => `${v}%`} />
                      <Bar dataKey="accuracy" fill="#E63946" radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  <p className="text-xs text-slate-500 mt-2">These chapters have less than 70% accuracy. Encourage focused practice!</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-2 border-[#06D6A0] rounded-2xl">
                <CardContent className="p-6 text-center">
                  <Award className="w-10 h-10 text-[#06D6A0] mx-auto mb-2" />
                  <p className="font-bold text-[#06D6A0]">Great Performance!</p>
                  <p className="text-sm text-slate-500">No weak areas detected. Keep it up!</p>
                </CardContent>
              </Card>
            )}
          </>
        )}
        {activeTab === 'create_test' && (
          <>
            <h3 className="font-bold text-[#1B4F8A] text-sm mb-3">Create Test for {student.name || 'Your Child'}</h3>
            <p className="text-xs text-slate-500 mb-3">Generate a personalized test paper and assign it to your child.</p>
            <TestGenerator classLevel={data?.student_profile?.class_level || 6} mode="parent"
              onSave={async (questions, title, totalMarks) => {
                try {
                  const res = await fetch(`${API}/parent/create-assignment`, {
                    method: 'POST',
                    headers: { ...getHeaders(), 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      title,
                      question_ids: questions.map(q => q.id),
                      total_marks: totalMarks,
                      class_level: data?.student_profile?.class_level || 6,
                    }),
                  });
                  if (res.ok) {
                    toast.success('Test assigned to your child!');
                  } else {
                    toast.error('Failed to create assignment');
                  }
                } catch { toast.error('Save failed'); }
              }}
            />
          </>
        )}
      </div>
    </div>
  );
}
